// Public payload + mapper for the `lot:{id}:bids:ledger` channel introduced
// in ADR 009 / BL-408. Each broadcast event is one appended bid; the
// LedgerContext on the consumer side handles ordering + dedupe by id.
//
// MOSTLY public-safe. The channel is anon-subscribable, so this shape
// excludes the most sensitive fields — raw `user_id`, `ip_address`,
// `tenant_id`, and the bidder's REAL NAME never appear here. Clerks
// who need name + raw user_id refetch via the authenticated
// `/api/bids/enrichment` endpoint and join client-side after the
// broadcast tells them "a bid happened."
//
// `bidderAlias` IS on the wire, formatted as "Bidder #<number>" when
// a `bid_registrations.paddle_number` exists for the auction (else
// "Bidder <6-char hash>"). Pre-BL-408 the bidder's own /api/bids/history
// returned the same paddle alias to authenticated lot watchers; the
// BL-408 broadcast widens that to "any anon-key holder who knows the
// lotId". This widened exposure is tracked under BL-391 Part 3
// (channel-level subscriber auth + anon-key retirement) and is the
// accepted intermediate state until Part 3 ships. Do NOT add fully-
// privileged fields (real name, raw user_id, ip, payment auth) to this
// payload — the widened exposure stops at the existing paddle class.
//
// The bidder UI's "is this mine?" check uses the opaque `bidderHash` —
// the same deterministic hash the server returns on
// `GET /api/bids/history` — so a bidder can compare against their own
// hash from the initial-fetch payload without the raw user_id ever
// touching the wire.

import type Decimal from 'decimal.js';

import type { MoneyWire } from '../../isomorphic/money/codec';
import type { LedgerItem } from '../../stores/realtime/ledger-store';
import { encodeRowMoney } from './money-wire-amount';

/**
 * Lifecycle states a bid can be in. Sourced from the DB
 * `bid_status` enum; broadcasting `withdrawn` IS intentional — the
 * bidder UI renders withdrawn bids with a strikethrough so the
 * subscriber needs to learn about the transition.
 *
 * BL-1407 dropped `rejected`. It was in the enum but nothing ever wrote one —
 * most likely orphaned when the Live Webcast clerking console was deleted
 * (BL-1128), a live clerk voiding a bid being the natural writer.
 */
export type BidLedgerStatus =
    | 'accepted'
    | 'outbid'
    | 'withdrawn';

/**
 * Public-safe shape of a single bid as it travels on the ledger channel.
 * Extends `LedgerItem` so the LedgerStore's ordering + dedupe (`id` +
 * `createdAt`) works without any consumer-side adapter.
 */
export interface PublicBidLedgerEntry extends LedgerItem {
    id: string;
    createdAt: string;
    /** `bids.lot_seq` — the PER-LOT monotonic sequence. THE display ordering key
     *  for this feed AND its catch-up cursor (BL-1631).
     *
     *  It replaced `bids.seq` as this payload's source. `bids.seq` is a global
     *  `bigserial`, which orders correctly (BUG-1282: `createdAt` ties to the
     *  microsecond within a transaction and `amount` ties in a max-bid duel, so
     *  neither can) but is SPARSE per lot — lot A jumps 7 → 22 because other lots
     *  took 8–21. A client cannot tell that from a gap, which is why it could
     *  never be the cursor. This one is dense and 1-based per lot, so `N+1`
     *  means "next" and `N+2` means "you missed one".
     *
     *  Public-safe: an opaque per-lot counter, no bidder information. It rides
     *  both this broadcast and the ledger's REST catch-up (`/api/bids/history`)
     *  — ONE delivery path (catch-up is this ledger's bootstrap), exactly as
     *  `amount` and `status` already do, not a second source to drift against. */
    seq: number;
    lotId: string;
    /** The bid amount in its own currency — `bids.amount` + `bids.currency`,
     *  encoded once by `encodeRowMoney` (BL-1942, ADR 060 — Money Type
     *  § 2.3). Public-safe: the currency is the auction house's, already shown
     *  on every price on the lot page. Decode with `fromWire` on the client. */
    amount: MoneyWire;
    status: BidLedgerStatus;
    placedByClerk: boolean;
    isAuto: boolean;
    /** Opaque per-bidder identifier. Same hash the server returns from
     *  GET /api/bids/history so the client can compare against the
     *  bidder's own hash (loaded with the initial fetch) to derive
     *  "isYou" without the raw user_id ever leaving the server. */
    bidderHash: string;
    /** Anonymized display name (e.g. "Bidder #42" or a Faker-style alias).
     *  Server-generated; client never knows the real name. */
    bidderAlias: string;
    /** Coarse location (e.g. "California, US") — server enforces the
     *  coarseness. `null` when the bidder opted out or the location is
     *  unknown. */
    bidderLocation: string | null;
}

/**
 * Input row shape — duck-typed to match the Drizzle select the publisher
 * helper runs against `bids`. Listing only the public-safe fields the
 * mapper actually reads keeps the helper free to project wider rows
 * without dragging more PII through the function boundary.
 */
export interface BidLedgerEntryRow {
    id: string;
    /** `bids.lot_seq` — the per-lot sequence, projected so the mapper carries the
     *  ordering key through. Sourced from `lot_seq`, NOT the global `bids.seq`
     *  (BL-1631). */
    seq: number;
    lotId: string;
    /** `bids.amount` — numeric(18,4) decoded to an exact Decimal by the
     *  moneyNumeric custom type (ADR 060 — Money Type, BL-1917). */
    amount: Decimal;
    /** `bids.currency` — the row's own ISO 4217 code (BL-1807). Rule #40: a
     *  column of the same `bids` row, so it adds no fan-in source. */
    currency: string;
    status: string;
    createdAt: string;
    placedByClerk: boolean;
    isAuto: boolean;
}

/**
 * Enrichment context produced server-side per bid: opaque hash + display
 * alias + coarse location, derived from the bid's `user_id` via the same
 * service `/api/bids/history` uses today. Kept separate from the row so
 * the mapper stays a pure shape-transform — the publisher helper
 * resolves enrichment alongside the row read and passes both in.
 */
export interface BidLedgerEntryEnrichment {
    bidderHash: string;
    bidderAlias: string;
    bidderLocation: string | null;
}

// This set MUST track the `bid_status` enum exactly (same contract as
// `admin-max-bids.ts`, BL-1397). It is a runtime string set, so tsc cannot see
// through it to the DB enum — a value left stale here makes both ledger mappers
// return null for every row carrying it, which drops the bid from the feed and
// gaps the ledger `seq` instead of failing loudly. ONE set for both ledgers
// (BL-2018): the public and admin mappers each carried a copy before.
const BID_LEDGER_STATUSES: ReadonlySet<string> = new Set<BidLedgerStatus>(
    ['accepted', 'outbid', 'withdrawn'],
);

/**
 * Narrow a `bids.status` value to a status the bid ledgers carry.
 *
 * @param status - The raw column value.
 * @returns True when both ledger payloads may carry it.
 */
export function isBidLedgerStatus(status: string): status is BidLedgerStatus {
    return BID_LEDGER_STATUSES.has(status);
}

/**
 * Map a single bid row + its enrichment context to the public ledger
 * payload. Returns `null` for rows with an unknown status — the DB enum
 * prevents this today, but dropping is the safer reaction than emitting
 * an item with an invalid status to every subscriber.
 */
export function mapBidLedgerEntryPublic(
    row: BidLedgerEntryRow,
    enrichment: BidLedgerEntryEnrichment,
): PublicBidLedgerEntry | null {
    if (!isBidLedgerStatus(row.status)) {
        return null;
    }
    return {
        id: row.id,
        createdAt: row.createdAt,
        seq: row.seq,
        lotId: row.lotId,
        amount: encodeRowMoney(row),
        status: row.status,
        placedByClerk: row.placedByClerk,
        isAuto: row.isAuto,
        bidderHash: enrichment.bidderHash,
        bidderAlias: enrichment.bidderAlias,
        bidderLocation: enrichment.bidderLocation,
    };
}
